/* globals seConfirm seAlert */
/**
 * The main module for the visual SVG this.
 *
 * @license MIT
 *
 * @copyright 2010 Alexis Deveria
 * 2010 Pavol Rusnak
 * 2010 Jeff Schiller
 * 2010 Narendra Sisodiya
 * 2014 Brett Zamir
 * 2020 OptimistikSAS
 * @module SVGEditor
 */

// First, before anything that takes $click from SvgCanvas (see clickOnce.js).
import './clickOnce.js'
import './components/index.js'
import './dialogs/index.js'

import { isMac } from '@svgedit/svgcanvas/common/browser'

import SvgCanvas from '@svgedit/svgcanvas'
import ConfigObj from './ConfigObj.js'
import EditorStartup from './EditorStartup.js'
import LeftPanel from './panels/LeftPanel.js'
import TopPanel from './panels/TopPanel.js'
import BottomPanel from './panels/BottomPanel.js'
import LayersPanel from './panels/LayersPanel.js'
import MainMenu from './MainMenu.js'
import { getParentsUntil } from '@svgedit/svgcanvas/common/util.js'

const { $id, $click, decode64, blankPageObjectURL } = SvgCanvas

/**
 *
 */
class Editor extends EditorStartup {
  /**
   *
   */
  constructor(div = null, contourWidth, contourHeight, title,
    svg64encoded, backgroundContour64Encoded, backgroundContourFileExtension, maxObjectsCount,
    costPerObject, currency, isBlackWhite, specifyMaterials, materialColors, restrictedPaletteColors) {
    super(div)
    /**
     * @type {boolean}
     */
    this.langChanged = false
    /**
     * @type {boolean}
     */
    this.showSaveWarning = false
    /**
     * Will be set to a boolean by `ext-storage.js`
     * @type {"ignore"|"waiting"|"closed"}
     */
    this.storagePromptState = 'ignore'
    /**
     * document title
     */
    this.title = 'untitled.svg'

    this.productContour = {
      width: contourWidth,
      height: contourHeight
    }

    console.log('contourWidth in editor', contourWidth)
    console.log('contourHeight in editor', contourHeight)

    this.backgroundContour64Encoded = backgroundContour64Encoded
    this.backgroundContourFileExtension = backgroundContourFileExtension

    //console.log("contourSvg passed to Editor.js: " + this.contourSvg)
    // console.log("backgroundImage64encoded passed to Editor.js: " + this.backgroundImage)

    this.svg64encoded = svg64encoded
    this.maxObjectsCount = maxObjectsCount
    this.costPerObject = costPerObject
    this.currency = currency
    console.log('Editor constructor isBlackWhite: ' + isBlackWhite)

    this.isBlackWhite = isBlackWhite
    this.specifyMaterials = specifyMaterials
    this.materialColors = materialColors
    this.restrictedPaletteColors = restrictedPaletteColors

    this.title = title
    this.decode64 = decode64
    this.svgCanvas = null
    this.$click = $click
    this.isReady = false
    this.customExportImage = false
    this.customExportPDF = false
    this.configObj = new ConfigObj(this)
    this.configObj.pref = this.configObj.pref.bind(this.configObj)
    this.setConfig = this.configObj.setConfig.bind(this.configObj)
    this.callbacks = []
    this.curContext = null
    this.exportWindowName = null
    this.docprops = false
    this.configObj.preferences = false
    this.canvMenu = null
    this.goodLangs = ['ar', 'cs', 'de', 'en', 'es', 'fa', 'fr', 'fy', 'hi', 'it', 'ja', 'nl', 'pl', 'pt-BR', 'ro', 'ru', 'sk', 'sl', 'sv', 'tr', 'uk', 'zh-CN', 'zh-TW']
    const modKey = (isMac() ? 'meta+' : 'ctrl+')
    this.shortcuts = [
      // Shortcuts not associated with buttons
      { key: 'ctrl+arrowleft', fn: () => { this.rotateSelected(0, 1) } },
      { key: 'ctrl+arrowright', fn: () => { this.rotateSelected(1, 1) } },
      { key: 'ctrl+shift+arrowleft', fn: () => { this.rotateSelected(0, 5) } },
      { key: 'ctrl+shift+arrowright', fn: () => { this.rotateSelected(1, 5) } },
      { key: 'shift+o', fn: () => { this.svgCanvas.cycleElement(0) } },
      { key: 'shift+p', fn: () => { this.svgCanvas.cycleElement(1) } },
      { key: 'tab', fn: () => { this.svgCanvas.cycleElement(0) } },
      { key: 'shift+tab', fn: () => { this.svgCanvas.cycleElement(1) } },
      { key: [modKey + 'arrowup', true], fn: () => { this.zoomImage(2) } },
      { key: [modKey + 'arrowdown', true], fn: () => { this.zoomImage(0.5) } },
      { key: [modKey + ']', true], fn: () => { this.moveUpDownSelected('Up') } },
      { key: [modKey + '[', true], fn: () => { this.moveUpDownSelected('Down') } },
      { key: ['arrowup', true], fn: () => { this.moveSelected(0, -1) } },
      { key: ['arrowdown', true], fn: () => { this.moveSelected(0, 1) } },
      { key: ['arrowleft', true], fn: () => { this.moveSelected(-1, 0) } },
      { key: ['arrowright', true], fn: () => { this.moveSelected(1, 0) } },
      { key: 'shift+arrowup', fn: () => { this.moveSelected(0, -10) } },
      { key: 'shift+arrowdown', fn: () => { this.moveSelected(0, 10) } },
      { key: 'shift+arrowleft', fn: () => { this.moveSelected(-10, 0) } },
      { key: 'shift+arrowright', fn: () => { this.moveSelected(10, 0) } },
      { key: ['alt+arrowup', true], fn: () => { this.svgCanvas.cloneSelectedElements(0, -1) } },
      { key: ['alt+arrowdown', true], fn: () => { this.svgCanvas.cloneSelectedElements(0, 1) } },
      { key: ['alt+arrowleft', true], fn: () => { this.svgCanvas.cloneSelectedElements(-1, 0) } },
      { key: ['alt+arrowright', true], fn: () => { this.svgCanvas.cloneSelectedElements(1, 0) } },
      { key: ['alt+shift+arrowup', true], fn: () => { this.svgCanvas.cloneSelectedElements(0, -10) } },
      { key: ['alt+shift+arrowdown', true], fn: () => { this.svgCanvas.cloneSelectedElements(0, 10) } },
      { key: ['alt+shift+arrowleft', true], fn: () => { this.svgCanvas.cloneSelectedElements(-10, 0) } },
      { key: ['alt+shift+arrowright', true], fn: () => { this.svgCanvas.cloneSelectedElements(10, 0) } },
      {
        key: ['delete/backspace', true],
        fn: () => {
          if (this.selectedElement || this.multiselected) {
            console.log('fn called')
            this.svgCanvas.deleteSelectedElements()
          }
        }
      },
      { key: 'a', fn: () => { this.svgCanvas.selectAllInCurrentLayer() } },
      { key: modKey + 'a', fn: () => { this.svgCanvas.selectAllInCurrentLayer() } },
      { key: modKey + 'x', fn: () => { this.cutSelected() } },
      { key: modKey + 'c', fn: () => { this.copySelected() } },
      { key: modKey + 'v', fn: () => { this.pasteInCenter() } }
    ]
    this.leftPanel = new LeftPanel(this)
    this.bottomPanel = new BottomPanel(this)
    this.topPanel = new TopPanel(this)
    this.layersPanel = new LayersPanel(this)
    this.mainMenu = new MainMenu(this)
    // makes svgEditor accessible as a global variable
    window.svgEditor = this
  } // end Constructor

  /**
   *
   * @param {string} str SVG string
   * @param {PlainObject} [opts={}]
   * @param {boolean} [opts.noAlert]
   * @throws {Error} Upon failure to load SVG
   * @returns {void}
   */
  loadSvgString(str, { noAlert } = {}) {
    const success = this.svgCanvas.setSvgString(str) !== false
    if (success) {
      this.updateCanvas()
      // Keep shape (configured dimensions) as the single source of truth and
      // fit the opened SVG artwork into that page size.
      this.enforceDocumentDimensionsAndFitContent()
      return
    }
    if (!noAlert) seAlert(this.i18next.t('notification.errorLoadingSVG'))
    throw new Error('Error loading SVG')
  }

  /**
   * Enforces the configured page size (shape dimensions) and scales/translates
   * the current layer content to fit inside that page.
   *
   * Intended to be called after initial load and after every Open.
   * @returns {void}
   */
  enforceDocumentDimensionsAndFitContent () {
    try {
      // init() is async; during early startup svgCanvas may not exist yet.
      if (!this.svgCanvas || typeof this.svgCanvas.setResolution !== 'function') {
        return
      }

      // IMPORTANT:
      // svgCanvas.setResolution() currently sets the root viewBox using `x/zoom` and `y/zoom`.
      // If we call it while zoom != 1, the resulting viewBox will not match the intended page
      // coordinate system, and any subsequent bbox-based fitting can compute wrong translations
      // (e.g. content jumping to negative X/Y on reopen).
      //
      // Therefore we temporarily normalize zoom to 1 while enforcing the page size and fitting.
      const prevZoom = (typeof this.svgCanvas.getZoom === 'function')
        ? Number(this.svgCanvas.getZoom())
        : 1
      const hadZoom = Number.isFinite(prevZoom) && prevZoom > 0
      if (hadZoom && Math.abs(prevZoom - 1) > 1e-6 && typeof this.svgCanvas.setCurrentZoom === 'function') {
        this.svgCanvas.setCurrentZoom(1)
      }

      const dims = this.configObj?.curConfig?.dimensions
      if (!dims || dims.length < 2) return

      // Dimensions may be numbers (px) or unit-bearing strings (e.g. "100mm").
      // svgCanvas.setResolution() will normalize units to its internal px user units.
      const dimW = dims[0]
      const dimH = dims[1]

      // Enforce document/page size regardless of what's inside the opened SVG.
      const ok = this.svgCanvas.setResolution(dimW, dimH)
      if (!ok) return

      const res = this.svgCanvas.getResolution && this.svgCanvas.getResolution()
      const pageWpx = res && Number(res.w)
      const pageHpx = res && Number(res.h)
      if (!Number.isFinite(pageWpx) || !Number.isFinite(pageHpx) || pageWpx <= 0 || pageHpx <= 0) return

      // If the loaded SVG was previously saved by this integration for the same shape,
      // do NOT auto-fit again on reopen. The content is already in the correct coordinate
      // system; re-fitting can introduce drift/offsets.
      let shouldFit = true
      try {
        const root = this.svgCanvas.getSvgContent && this.svgCanvas.getSvgContent()
        const authored = root && root.getAttribute && root.getAttribute('data-reihax-authored') === '1'
        const savedShape = root && root.getAttribute && (root.getAttribute('data-reihax-shape') || '').trim()
        const expectedShape = `${this.productContour?.width ?? ''}x${this.productContour?.height ?? ''}`
        if (authored && savedShape && expectedShape && savedShape === expectedShape) {
          shouldFit = false
        }
      } catch {
        shouldFit = true
      }

      // Always migrate legacy layer-level transforms to the wrapper approach,
      // even when shouldFit is false (re-opened authored SVGs still need migration).
      this.#migrateLegacyLayerTransform()

      // Structural repair, always: content must live inside the fit wrapper. Running this
      // only via #fitCurrentLayerToPage meant it was skipped whenever shouldFit was false,
      // which is exactly the reopened-design case where the artwork is stranded outside it.
      this.#adoptStrayLayerChildren()

      // The shouldFit shortcut trusts data-reihax-shape to mean "content already matches the
      // page". Verify that claim instead of assuming it: if the artwork actually lies outside
      // the page box, fit regardless of what the attribute says.
      if (!shouldFit && this.#layerContentEscapesPage(pageWpx, pageHpx)) {
        shouldFit = true
      }

      if (shouldFit) {
        this.#fitCurrentLayerToPage(pageWpx, pageHpx)
      }

      // The wrapper is how the fit is applied, not how the artwork should be left. Left in place it is a
      // group the user did not make: a tap selects the whole design as one object, and an ungrouped design
      // is regrouped by every reopen.
      this.#flattenFitWrapper()

      // Restore prior zoom (if any) and repaint.
      if (hadZoom && Math.abs(prevZoom - 1) > 1e-6 && typeof this.svgCanvas.setCurrentZoom === 'function') {
        this.svgCanvas.setCurrentZoom(prevZoom)
      }
      this.updateCanvas(true)
    } catch (e) {
      // Never block editor load/open on fitting issues.
      console.warn('enforceDocumentDimensionsAndFitContent failed', e)
    }
  }

  /**
   * Live-updates the document/page size when the physical-size fields are edited after the
   * canvas already exists (`initialize` only ever applies dimensions once, at construction).
   *
   * Spec decision: resizing the physical-size fields RESCALES the existing artwork to fit
   * the new page, it does not merely change the contour around unscaled content — this
   * reuses enforceDocumentDimensionsAndFitContent(), the same reconciliation path already
   * used on load/open, instead of introducing a second, divergent notion of "resize".
   * It never re-creates the editor/canvas, so in-progress artwork is never destroyed.
   *
   * @param {number|string} width
   * @param {number|string} height
   * @param {string} unit
   * @returns {void}
   */
  setContourDimensions (width, height, unit) {
    try {
      const u = (unit || 'cm').toString().trim().toLowerCase()
      const w = Number(width)
      const h = Number(height)
      if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return

      const documentWidth = `${w}${u}`
      const documentHeight = `${h}${u}`

      this.productContour = { width: documentWidth, height: documentHeight }
      if (this.configObj?.curConfig) {
        this.configObj.curConfig.dimensions = [documentWidth, documentHeight]
      }

      this.enforceDocumentDimensionsAndFitContent()
    } catch (e) {
      console.warn('setContourDimensions failed', e)
    }
  }

  /**
   * Migrates SVGs saved with the old approach (fit transform on the layer group) to the
   * new wrapper approach (fit transform on a child <g> inside the layer). This is needed
   * because SVG-Edit does not expect the layer group itself to carry a non-identity transform:
   * doing so breaks selection handle positioning (select.js Selector.resize).
   * @returns {void}
   */
  #migrateLegacyLayerTransform () {
    const svgContent = this.svgCanvas.getSvgContent?.()
    if (!svgContent) return
    const layer = svgContent.querySelector('g.layer') || svgContent.querySelector('g')
    if (!layer) return

    // Only migrate if the OLD per-layer data attributes exist
    const legacyFit = layer.getAttribute('data-reihax-fit')
    if (!legacyFit) return

    const legacyUser = (layer.getAttribute('data-reihax-user') || '').trim()
    layer.removeAttribute('data-reihax-fit')
    layer.removeAttribute('data-reihax-user')
    layer.removeAttribute('transform')

    // Check if a wrapper already exists (shouldn't happen, but be safe)
    let wrapper = layer.querySelector(':scope > [data-reihax-fit-wrapper]')
    if (!wrapper) {
      wrapper = document.createElementNS('http://www.w3.org/2000/svg', 'g')
      wrapper.setAttribute('data-reihax-fit-wrapper', '1')
      const children = [...layer.children]
      children.forEach(child => {
        if (child.tagName !== 'title') wrapper.appendChild(child)
      })
      layer.appendChild(wrapper)
    }

    const combined = legacyUser ? `${legacyFit} ${legacyUser}` : legacyFit
    wrapper.setAttribute('transform', combined)
    wrapper.setAttribute('data-reihax-fit', legacyFit)
    if (legacyUser) wrapper.setAttribute('data-reihax-user', legacyUser)
  }

  /**
   * Resolves the current layer group, or null when the canvas isn't ready.
   * @returns {?SVGGElement}
   */
  #getContentLayer () {
    const svgContent = this.svgCanvas?.getSvgContent?.()
    if (!svgContent || !svgContent.querySelector) return null
    return svgContent.querySelector('g.layer') || svgContent.querySelector('g')
  }

  /**
   * Ensures the fit wrapper exists and that ALL layer content lives inside it.
   *
   * This must run on every pass, not only when the wrapper is created: the wrapper is
   * serialised into every saved SVG, so on reload it already exists — and any content
   * drawn or loaded afterwards stays a sibling of it. A sibling is outside the element
   * the fit transform is applied to, so the transform is computed correctly and then
   * applied to nothing, leaving the artwork wherever it was (BUG-32 / BUG-30).
   *
   * @returns {?SVGGElement} the wrapper, or null when the layer isn't available
   */
  #adoptStrayLayerChildren () {
    const layer = this.#getContentLayer()
    if (!layer) return null

    let wrapper = layer.querySelector(':scope > [data-reihax-fit-wrapper]')
    if (!wrapper) {
      wrapper = document.createElementNS('http://www.w3.org/2000/svg', 'g')
      wrapper.setAttribute('data-reihax-fit-wrapper', '1')
      layer.appendChild(wrapper)
    }

    const strayChildren = [...layer.children]
      .filter(child => child !== wrapper && child.tagName !== 'title')
    strayChildren.forEach(child => wrapper.appendChild(child))

    return wrapper
  }

  /**
   * Dissolves the fit wrapper: its transform is handed to each child, and the children go back into the
   * layer in the wrapper's place. Nothing about how the artwork looks changes (a group's transform and the
   * same transform on each child draw identically); what changes is that the shapes are no longer one group.
   *
   * Done by hand rather than with svgcanvas's ungroup so it leaves no undo step and selects nothing — it
   * runs as part of loading a design, which should not start with something to undo.
   * @returns {void}
   */
  #flattenFitWrapper () {
    const layer = this.#getContentLayer()
    const wrapper = layer?.querySelector(':scope > [data-reihax-fit-wrapper]')
    if (!wrapper) return

    const wrapperTransform = (wrapper.getAttribute('transform') || '').trim()
    for (const child of [...wrapper.children]) {
      if (wrapperTransform) {
        const own = (child.getAttribute('transform') || '').trim()
        child.setAttribute('transform', own ? `${wrapperTransform} ${own}` : wrapperTransform)
      }
      layer.insertBefore(child, wrapper)
    }
    wrapper.remove()
    // Handles drawn around the old group would be left pointing at nothing.
    this.svgCanvas.clearSelection(true)
  }

  /**
   * True when the layer's content lies substantially outside the page box.
   *
   * Used as a safety net for the "already authored for this shape, don't re-fit" shortcut
   * in enforceDocumentDimensionsAndFitContent(). That shortcut assumes a matching
   * data-reihax-shape means the content is already in the page's coordinate system —
   * which is false for anything saved while BUG-29 was open, where only the declared
   * shape was updated and the artwork kept its original (much larger) coordinates.
   * Trusting the attribute alone leaves such designs looking empty.
   *
   * @param {number} pageW
   * @param {number} pageH
   * @returns {boolean}
   */
  #layerContentEscapesPage (pageW, pageH) {
    const layer = this.#getContentLayer()
    if (!layer || typeof layer.getBBox !== 'function') return false
    if (!Number.isFinite(pageW) || !Number.isFinite(pageH) || pageW <= 0 || pageH <= 0) return false

    try {
      const bb = layer.getBBox()
      if (!bb || !(bb.width > 0) || !(bb.height > 0)) return false

      // 2% slack so hairline strokes or rounding never trigger a re-fit on good content.
      const tolerance = Math.max(pageW, pageH) * 0.02
      return bb.x < -tolerance ||
             bb.y < -tolerance ||
             (bb.x + bb.width) > pageW + tolerance ||
             (bb.y + bb.height) > pageH + tolerance
    } catch {
      return false
    }
  }

  /**
   * Fits imported SVG content to the configured page dimensions by applying a
   * translate+scale transform to a wrapper <g> INSIDE the layer group.
   *
   * The layer group itself is intentionally left without a transform so that
   * SVG-Edit's selection system (select.js Selector.resize) can correctly compute
   * bounding boxes for selected elements. SVG-Edit does not expect the layer group
   * to carry a non-identity transform.
   *
   * We measure bounds via layer.getBBox() (not wrapper.getBBox()) because SVG spec
   * guarantees that a parent's getBBox() accounts for children's transform attributes,
   * whereas an element's own getBBox() may or may not include its own transform
   * (browser-dependent).
   *
   * @param {number} pageW
   * @param {number} pageH
   * @returns {void}
   */
  #fitCurrentLayerToPage (pageW, pageH) {
    const svgContent = this.svgCanvas.getSvgContent?.()
    if (!svgContent || !svgContent.querySelector) return

    const layer = svgContent.querySelector('g.layer') || svgContent.querySelector('g')
    if (!layer || typeof layer.getBBox !== 'function') return

    // Layer must never carry our fit transform — selection handles depend on it being identity.
    layer.removeAttribute('transform')

    // Adoption is a structural repair, not part of the fitting decision, so it lives in its
    // own method and is also called unconditionally from enforceDocumentDimensionsAndFitContent().
    const wrapper = this.#adoptStrayLayerChildren()
    if (!wrapper) return

    // Separate the user's original transform (stored once) from our auto-fit.
    const prevFit = (wrapper.getAttribute('data-reihax-fit') || '').trim()
    const existing = (wrapper.getAttribute('transform') || '').trim()

    let userTransform = (wrapper.getAttribute('data-reihax-user') || '').trim()
    if (!userTransform) {
      userTransform = prevFit && existing
        ? existing.replace(prevFit, '').trim()
        : existing
      wrapper.setAttribute('data-reihax-user', userTransform)
    }

    // Temporarily apply only the user transform so we measure the raw content bounds.
    const savedTransform = existing
    if (userTransform) {
      wrapper.setAttribute('transform', userTransform)
    } else {
      wrapper.removeAttribute('transform')
    }

    // Measure via the LAYER's getBBox: since the layer has no transform, its coordinate
    // space IS the page coordinate space, and its getBBox() accounts for the wrapper's
    // userTransform, giving us the effective content bounds.
    let bbox = null
    try {
      const bb = layer.getBBox()
      if (bb && bb.width > 0 && bb.height > 0) {
        bbox = { x: bb.x, y: bb.y, width: bb.width, height: bb.height }
      }
    } catch { /* ignore */ }

    if (!bbox) {
      if (savedTransform) wrapper.setAttribute('transform', savedTransform)
      else wrapper.removeAttribute('transform')
      return
    }

    // Skip re-fitting if content already occupies the page.
    const sizeTolerance = 0.01
    const posTolerance = 0.5
    const alreadyFits =
      Math.abs(bbox.width - pageW) / pageW < sizeTolerance &&
      Math.abs(bbox.height - pageH) / pageH < sizeTolerance &&
      Math.abs(bbox.x) <= posTolerance &&
      Math.abs(bbox.y) <= posTolerance &&
      Math.abs((bbox.x + bbox.width) - pageW) <= posTolerance &&
      Math.abs((bbox.y + bbox.height) - pageH) <= posTolerance

    if (alreadyFits) {
      wrapper.removeAttribute('data-reihax-fit')
      return
    }

    const scale = Math.min(pageW / bbox.width, pageH / bbox.height)
    if (!Number.isFinite(scale) || scale <= 0) {
      if (savedTransform) wrapper.setAttribute('transform', savedTransform)
      else wrapper.removeAttribute('transform')
      return
    }

    // fitTransform is prepended so transforms apply right-to-left:
    //   1. userTransform positions content in its authored coordinate system
    //   2. scale(s) scales the authored content
    //   3. translate(tx,ty) centers the scaled result on the page
    const tX = (pageW - bbox.width * scale) / 2 - bbox.x * scale
    const tY = (pageH - bbox.height * scale) / 2 - bbox.y * scale
    const fitTransform = `translate(${tX} ${tY}) scale(${scale})`

    const combined = userTransform ? `${fitTransform} ${userTransform}` : fitTransform
    wrapper.setAttribute('transform', combined)
    wrapper.setAttribute('data-reihax-fit', fitTransform)
  }

  /**
   * All methods are optional.
   * @interface module:SVGthis.CustomHandler
   * @type {PlainObject}
   */
  /**
   * Its responsibilities are:
   *  - invoke a file chooser dialog in 'open' mode
   *  - let user pick a SVG file
   *  - calls [svgCanvas.setSvgString()]{@link module:svgcanvas.SvgCanvas#setSvgString} with the string contents of that file.
   * Not passed any parameters.
   * @function module:SVGthis.CustomHandler#open
   * @returns {void}
   */
  /**
   * Its responsibilities are:
   *  - accept the string contents of the current document
   *  - invoke a file chooser dialog in 'save' mode
   *  - save the file to location chosen by the user.
   * @function module:SVGthis.CustomHandler#save
   * @param {external:Window} win
   * @param {module:svgcanvas.SvgCanvas#event:saved} svgStr A string of the SVG
   * @listens module:svgcanvas.SvgCanvas#event:saved
   * @returns {void}
   */
  /**
   * Its responsibilities (with regard to the object it is supplied in its 2nd argument) are:
   *  - inform user of any issues supplied via the "issues" property
   *  - convert the "svg" property SVG string into an image for export;
   *    utilize the properties "type" (currently 'PNG', 'JPEG', 'BMP',
   *    'WEBP', 'PDF'), "mimeType", and "quality" (for 'JPEG' and 'WEBP'
   *    types) to determine the proper output.
   * @function module:SVGthis.CustomHandler#exportImage
   * @param {external:Window} win
   * @param {module:svgcanvas.SvgCanvas#event:exported} data
   * @listens module:svgcanvas.SvgCanvas#event:exported
   * @returns {void}
   */
  /**
   * @function module:SVGthis.CustomHandler#exportPDF
   * @param {external:Window} win
   * @param {module:svgcanvas.SvgCanvas#event:exportedPDF} data
   * @listens module:svgcanvas.SvgCanvas#event:exportedPDF
   * @returns {void}
   */

  /**
   * @function module:SVGthis.randomizeIds
   * @param {boolean} arg
   * @returns {void}
   */
  randomizeIds (arg) {
    this.svgCanvas.randomizeIds(arg)
  }

  /** @lends module:SVGEditor~Actions */
  /**
   * @returns {void}
   */
  setAll () {
    const keyHandler = {} // will contain the action for each pressed key

    this.shortcuts.forEach(shortcut => {
      // Bind function to shortcut key
      if (shortcut.key) {
        // Set shortcut based on options
        let keyval = shortcut.key
        let pd = false
        if (Array.isArray(shortcut.key)) {
          keyval = shortcut.key[0]
          if (shortcut.key.length > 1) {
            pd = shortcut.key[1]
          }
        }
        keyval = String(keyval)
        const { fn } = shortcut
        keyval.split('/').forEach(key => {
          keyHandler[key] = { fn, pd }
        })
      }
      return true
    })
    // register the keydown event
    document.addEventListener('keydown', e => {
      // only track keyboard shortcuts for the body containing the SVG-Editor
      if (e.target.nodeName !== 'BODY') return
      // normalize key
      const key = `${e.altKey ? 'alt+' : ''}${e.shiftKey ? 'shift+' : ''}${
        e.metaKey ? 'meta+' : ''
      }${e.ctrlKey ? 'ctrl+' : ''}${e.key.toLowerCase()}`
      // return if no shortcut defined for this key
      if (!keyHandler[key]) return
      // launch associated handler and preventDefault if necessary
      keyHandler[key].fn()
      if (keyHandler[key].pd) {
        e.preventDefault()
      }
    })

    // Misc additional actions

    // Make 'return' keypress trigger the change event
    const elements = document.getElementsByClassName('attr_changer')
    Array.from(elements).forEach(function (element) {
      element.addEventListener('keydown', function (evt) {
        evt.currentTarget.dispatchEvent(new Event('change'))
        evt.preventDefault()
      })
    })
    $id('image_url').addEventListener('keydown', function (evt) {
      evt.currentTarget.dispatchEvent(new Event('change'))
      evt.preventDefault()
    })
  }

  // parents() https://stackoverflow.com/a/12981248
  getParents (el, parentSelector /* optional */) {
    // If no parentSelector defined will bubble up all the way to *document*
    if (parentSelector === undefined) {
      parentSelector = document
    }

    const parents = []
    let p = el.parentNode

    while (p !== parentSelector) {
      const o = p
      parents.push(o)
      p = o.parentNode
    }
    parents.push(parentSelector) // Push that parentSelector you wanted to stop at

    return parents
  }

  /**
   * @param {string} sel Selector to match
   * @returns {module:SVGthis.ToolButton}
   */
  getButtonData (sel) {
    return Object.values(this.shortcuts).find(btn => {
      return btn.sel === sel
    })
  }

  /**
   * @param {external:Window} win
   * @param {module:svgcanvas.SvgCanvas#event:exported} data
   * @listens module:svgcanvas.SvgCanvas#event:exported
   * @returns {void}
   */
  exportHandler (win, data) {
    const { issues, exportWindowName } = data

    this.exportWindow = window.open(blankPageObjectURL || '', exportWindowName) // A hack to get the window via JSON-able name without opening a new one

    if (!this.exportWindow || this.exportWindow.closed) {
      seAlert(this.i18next.t('notification.popupWindowBlocked'))
      return
    }

    this.exportWindow.location.href = data.bloburl || data.datauri
    const done = this.configObj.pref('export_notice_done')
    if (done !== 'all') {
      let note = this.i18next.t('notification.saveFromBrowser', {
        type: data.type
      })

      // Check if there are issues
      if (issues.length) {
        const pre = '\n \u2022 '
        note +=
          '\n\n' +
          this.i18next.t('notification..noteTheseIssues') +
          pre +
          issues.join(pre)
      }

      // Note that this will also prevent the notice even though new issues may appear later.
      // May want to find a way to deal with that without annoying the user
      this.configObj.pref('export_notice_done', 'all')
      seAlert(note)
    }
  }

  /**
   *
   * @param {string} color
   * @param {string} url
   * @returns {void}
   */
  setBackground (color, url) {
    // if (color == this.configObj.pref('bkgd_color') && url == this.configObj.pref('bkgd_url')) { return; }
    this.configObj.pref('bkgd_color', color)
    this.configObj.pref('bkgd_url', url, true)

    // This should be done in  this.svgCanvas.js for the borderRect fill
    this.svgCanvas.setBackground(color, url)
  }

  /**
   * @function module:SVGthis.updateCanvas
   * @param {boolean} center
   * @param {module:math.XYObject} newCtr
   * @returns {void}
   */
  updateCanvas (center, newCtr) {
    const zoom = this.svgCanvas.getZoom()
    const { workarea } = this
    const cnvs = $id('svgcanvas')

    let w = parseFloat(getComputedStyle(workarea, null).width.replace('px', ''))
    let h = parseFloat(
      getComputedStyle(workarea, null).height.replace('px', '')
    )
    const wOrig = w
    const hOrig = h
    const oldCtr = {
      x: workarea.scrollLeft + wOrig / 2,
      y: workarea.scrollTop + hOrig / 2
    }
    const multi = this.configObj.curConfig.canvas_expansion
    w = Math.max(wOrig, this.svgCanvas.contentW * zoom * multi)
    h = Math.max(hOrig, this.svgCanvas.contentH * zoom * multi)

    if (w === wOrig && h === hOrig) {
      workarea.style.overflow = 'hidden'
    } else {
      workarea.style.overflow = 'scroll'
    }

    const oldCanY =
      parseFloat(getComputedStyle(cnvs, null).height.replace('px', '')) / 2
    const oldCanX =
      parseFloat(getComputedStyle(cnvs, null).width.replace('px', '')) / 2

    cnvs.style.width = w + 'px'
    cnvs.style.height = h + 'px'
    const newCanY = h / 2
    const newCanX = w / 2
    const offset = this.svgCanvas.updateCanvas(w, h)

    const ratio = newCanX / oldCanX

    const scrollX = w / 2 - wOrig / 2
    const scrollY = h / 2 - hOrig / 2

    if (!newCtr) {
      const oldDistX = oldCtr.x - oldCanX
      const newX = newCanX + oldDistX * ratio

      const oldDistY = oldCtr.y - oldCanY
      const newY = newCanY + oldDistY * ratio

      newCtr = {
        x: newX,
        y: newY
      }
    } else {
      newCtr.x += offset.x
      newCtr.y += offset.y
    }

    if (center) {
      // Go to top-left for larger documents
      if (
        this.svgCanvas.contentW >
        parseFloat(getComputedStyle(workarea, null).width.replace('px', ''))
      ) {
        // Top-left
        workarea.scrollLeft = offset.x - 10
        workarea.scrollTop = offset.y - 10
      } else {
        // Center
        workarea.scrollLeft = scrollX
        workarea.scrollTop = scrollY
      }
    } else {
      workarea.scrollLeft = newCtr.x - wOrig / 2
      workarea.scrollTop = newCtr.y - hOrig / 2
    }
    if (this.configObj.curConfig.showRulers) {
      this.rulers.updateRulers(cnvs, zoom)
      workarea.scroll()
    }

    if (
      this.configObj.urldata.storagePrompt !== true &&
      this.storagePromptState === 'ignore'
    ) {
      if ($id('dialog_box') != null) $id('dialog_box').style.display = 'none'
    }
  }

  /**
   *
   * @returns {void}
   */
  updateWireFrame () {
    const rule = `
      #workarea.wireframe #svgcontent * {
        stroke-width: ${1 / this.svgCanvas.getZoom()}px;
      }
    `
    if (document.querySelectorAll('#wireframe_rules').length > 0) {
      document.querySelector(
        '#wireframe_rules'
      ).textContent = this.workarea.classList.contains('wireframe') ? rule : ''
    }
  }

  // called when we've selected a different element
  /**
   *
   * @param {external:Window} win
   * @param {module:svgcanvas.SvgCanvas#event:selected} elems Array of elements that were selected
   * @listens module:svgcanvas.SvgCanvas#event:selected
   * @fires module:svgcanvas.SvgCanvas#event:ext_selectedChanged
   * @returns {void}
   */
  selectedChanged (win, elems) {
    const mode = this.svgCanvas.getMode()
    if (mode === 'select') {
      this.leftPanel.clickSelect()
    }
    const isNode = mode === 'pathedit'
    // if this.elems[1] is present, then we have more than one element
    this.selectedElement = elems.length === 1 || !elems[1] ? elems[0] : null
    this.multiselected = elems.length >= 2 && !!elems[1]
    if (this.selectedElement && !isNode) {
      this.topPanel.update()
    } // if (elem)

    // Deal with pathedit mode
    this.topPanel.togglePathEditMode(isNode, elems)
    this.topPanel.updateContextPanel()
    this.svgCanvas.runExtensions(
      'selectedChanged',
      /** @type {module:svgcanvas.SvgCanvas#event:ext_selectedChanged} */ {
        elems,
        selectedElement: this.selectedElement,
        multiselected: this.multiselected
      }
    )
  }

  // Call when part of element is in process of changing, generally
  // on mousemove actions like rotate, move, etc.
  /**
   * @param {external:Window} win
   * @param {module:svgcanvas.SvgCanvas#event:transition} elems
   * @listens module:svgcanvas.SvgCanvas#event:transition
   * @fires module:svgcanvas.SvgCanvas#event:ext_elementTransition
   * @returns {void}
   */
  elementTransition (win, elems) {
    const mode = this.svgCanvas.getMode()
    const elem = elems[0]

    if (!elem) {
      return
    }

    this.multiselected = elems.length >= 2 && elems[1]
    // Only updating fields for single elements for now
    if (!this.multiselected) {
      switch (mode) {
        case 'rotate': {
          const ang = this.svgCanvas.getRotationAngle(elem)
          $id('angle').value = ang
          ang === 0
            ? $id('tool_reorient').classList.add('disabled')
            : $id('tool_reorient').classList.remove('disabled')
          break
        }
      }
    }
    this.svgCanvas.runExtensions(
      'elementTransition',
      /** @type {module:svgcanvas.SvgCanvas#event:ext_elementTransition} */ {
        elems
      }
    )
  }

  // called when any element has changed
  /**
   * @param {external:Window} win
   * @param {Array<PlainObject>} elems
   * @listens module:svgcanvas.SvgCanvas#event:changed
   * @fires module:svgcanvas.SvgCanvas#event:ext_elementChanged
   * @returns {void}
   */
  elementChanged (win, elems) {
    const mode = this.svgCanvas.getMode()
    if (mode === 'select') {
      this.leftPanel.clickSelect()
    }

    elems.forEach(elem => {
      const isSvgElem = elem?.tagName === 'svg'
      if (isSvgElem || this.svgCanvas.isLayer(elem)) {
        this.layersPanel.populateLayers()
        // if the element changed was the svg, then it could be a resolution change
        if (isSvgElem) {
          this.updateCanvas()
        }
        // Update selectedElement if element is no longer part of the image.
        // This occurs for the text elements in Firefox
      } else if (elem && !this.selectedElement?.parentNode) {
        this.selectedElement = elem
      }
    })

    this.showSaveWarning = true

    // we update the contextual panel with potentially new
    // positional/sizing information (we DON'T want to update the
    // toolbar here as that creates an infinite loop)
    // also this updates the history buttons

    // we tell it to skip focusing the text control if the
    // text element was previously in focus
    this.topPanel.updateContextPanel()

    // In the event a gradient was flipped:
    if (this.selectedElement && mode === 'select') {
      this.bottomPanel.updateColorpickers()
    }

    this.svgCanvas.runExtensions(
      'elementChanged',
      /** @type {module:svgcanvas.SvgCanvas#event:ext_elementChanged} */ {
        elems
      }
    )
  }

  /**
   * @returns {void}
   */
  elementRenamed (win, renameObj) {
    this.svgCanvas.runExtensions(
      'elementRenamed',
      /** @type {module:svgcanvas.SvgCanvas#event:ext_elementRenamed} */ {
        renameObj
      }
    )
  }

  /**
   * @returns {void}
   */
  afterClear (win) {
    this.svgCanvas.runExtensions(
      'afterClear'
    )
  }

  /**
   * @returns {void}
   */
  beforeClear (win) {
    this.svgCanvas.runExtensions(
      'beforeClear'
    )
  }

  /**
   * @returns {void}
   */
  zoomDone () {
    for (const el of this.svgCanvas.selectedElements) {
      this.svgCanvas.selectorManager.requestSelector(el).resize()
    }
    this.updateWireFrame()
  }

  /**
   * @typedef {PlainObject} module:SVGthis.BBoxObjectWithFactor (like `DOMRect`)
   * @property {Float} x
   * @property {Float} y
   * @property {Float} width
   * @property {Float} height
   * @property {Float} [factor] Needed if width or height are 0
   * @property {Float} [zoom]
   * @see module:svgcanvas.SvgCanvas#event:zoomed
   */

  /**
   * @function module:svgcanvas.SvgCanvas#zoomChanged
   * @param {external:Window} win
   * @param {module:svgcanvas.SvgCanvas#event:zoomed} bbox
   * @param {boolean} autoCenter
   * @listens module:svgcanvas.SvgCanvas#event:zoomed
   * @returns {void}
   */
  zoomChanged (win, bbox, autoCenter) {
    const scrbar = 15
    const zInfo = this.svgCanvas.setBBoxZoom(
      bbox,
      parseFloat(
        getComputedStyle(this.workarea, null).width.replace('px', '')
      ) - scrbar,
      parseFloat(
        getComputedStyle(this.workarea, null).height.replace('px', '')
      ) - scrbar
    )
    if (!zInfo) {
      return
    }
    const zoomlevel = zInfo.zoom
    const bb = zInfo.bbox

    if (zoomlevel < 0.001) {
      this.changeZoom(0.1)
      return
    }

    $id('zoom').value = (this.svgCanvas.getZoom() * 100).toFixed(1)

    if (autoCenter) {
      this.updateCanvas()
    } else {
      this.updateCanvas(false, {
        x: bb.x * zoomlevel + (bb.width * zoomlevel) / 2,
        y: bb.y * zoomlevel + (bb.height * zoomlevel) / 2
      })
    }

    if (this.svgCanvas.getMode() === 'zoom' && bb.width) {
      // Go to select if a zoom box was drawn
      this.leftPanel.clickSelect()
    }

    this.zoomDone()
  }

  /**
   * @param {external:Window} win
   * @param {module:svgcanvas.SvgCanvas#event:contextset} context
   * @listens module:svgcanvas.SvgCanvas#event:contextset
   * @returns {void}
   */
  contextChanged (win, context) {
    let linkStr = ''
    if (context) {
      let str = ''
      linkStr =
        '<a href="#" data-root="y">' +
        this.svgCanvas.getCurrentDrawing().getCurrentLayerName() +
        '</a>'
      const parentsUntil = getParentsUntil(context, '#svgcontent')
      parentsUntil.forEach(function (parent) {
        if (parent.id) {
          str += ' > ' + parent.id
          linkStr +=
            parent !== context
              ? ` > <a href="#">${parent.id}</a>`
              : ` > ${parent.id}`
        }
      })

      this.curContext = str
    } else {
      this.curContext = null
    }
    $id('cur_context_panel').style.display = context ? 'block' : 'none'
    $id('cur_context_panel').innerHTML = linkStr
  }

  /**
   * @function module:SVGEditor.setIcon
   * @param {string|Element|external:jQuery} elem
   * @param {string|external:jQuery} iconId
   * @returns {void}
   */
  setIcon (elem, iconId) {
    const img = document.createElement('img')
    img.src = this.configObj.curConfig.imgPath + iconId
    const icon = typeof iconId === 'string' ? img : iconId.cloneNode(true)
    if (!icon) {
      // Todo: Investigate why this still occurs in some cases
      console.warn('NOTE: Icon image missing: ' + iconId)
      return
    }
    // empty()
    while ($id(elem).firstChild) {
      $id(elem).removeChild($id(elem).firstChild)
    }
    $id(elem).appendChild(icon)
  }

  /**
   * @param {external:Window} win
   * @param {module:svgcanvas.SvgCanvas#event:extension_added} ext
   * @listens module:svgcanvas.SvgCanvas#event:extension_added
   * @returns {Promise<void>|void} Resolves to `undefined`
   */
  async extAdded (win, ext) {
    if (!ext) {
      return undefined
    }
    let cbCalled = false

    /**
     *
     * @returns {void}
     */
    const runCallback = () => {
      if (ext.callback && !cbCalled) {
        cbCalled = true
        ext.callback.call(this)
      }
    }

    if (ext.events) {
      this.leftPanel.add(ext.events.id, ext.events.click)
    }
    return runCallback()
  }

  /**
   * @param {Float} multiplier
   * @returns {void}
   */
  zoomImage (multiplier) {
    const resolution = this.svgCanvas.getResolution()
    multiplier = multiplier ? resolution.zoom * multiplier : 1
    // setResolution(res.w * multiplier, res.h * multiplier, true);
    $id('zoom').value = (multiplier * 100).toFixed(1)
    this.svgCanvas.setCurrentZoom(multiplier)
    this.zoomDone()
    this.updateCanvas(true)
  }

  /**
   *
   * @returns {void}
   */
  cutSelected () {
    if (this.selectedElement || this.multiselected) {
      this.svgCanvas.cutSelectedElements()
    }
  }

  /**
   * @function copySelected
   * @returns {void}
   */
  copySelected () {
    if (this.selectedElement || this.multiselected) {
      this.svgCanvas.copySelectedElements()
    }
  }

  /**
   *
   * @returns {void}
   */
  pasteInCenter () {
    const { workarea } = this
    const zoom = this.svgCanvas.getZoom()
    const x =
      (workarea.scrollLeft +
        parseFloat(getComputedStyle(workarea, null).width.replace('px', '')) /
          2) /
        zoom -
      this.svgCanvas.contentW
    const y =
      (workarea.scrollTop +
        parseFloat(getComputedStyle(workarea, null).height.replace('px', '')) /
          2) /
        zoom -
      this.svgCanvas.contentH
    this.svgCanvas.pasteElements('point', x, y)
  }

  /**
   * @param {"Up"|"Down"} dir
   * @returns {void}
   */
  moveUpDownSelected (dir) {
    if (this.selectedElement) {
      this.svgCanvas.moveUpDownSelected(dir)
    }
  }

  /**
   * @param {Float} dx
   * @param {Float} dy
   * @returns {void}
   */
  moveSelected (dx, dy) {
    if (this.selectedElement || this.multiselected) {
      if (this.configObj.curConfig.gridSnapping) {
        // Use grid snap value regardless of zoom level
        const multi =
          this.svgCanvas.getZoom() * this.configObj.curConfig.snappingStep
        dx *= multi
        dy *= multi
      }
      this.svgCanvas.moveSelectedElements(dx, dy)
    }
  }

  /**
   *
   * @returns {void}
   */
  selectNext () {
    this.svgCanvas.cycleElement(1)
  }

  /**
   *
   * @returns {void}
   */
  selectPrev () {
    this.svgCanvas.cycleElement(0)
  }

  /**
   * @param {0|1} cw
   * @param {Integer} step
   * @returns {void}
   */
  rotateSelected (cw, step) {
    if (!this.selectedElement || this.multiselected) {
      return
    }
    if (!cw) {
      step *= -1
    }
    const angle = Number.parseFloat($id('angle').value) + step
    this.svgCanvas.setRotationAngle(angle)
    this.topPanel.updateContextPanel()
  }

  /**
   *
   * @returns {void}
   */
  hideSourceEditor () {
    const $editorDialog = $id('se-svg-editor-dialog')
    $editorDialog.setAttribute('dialog', 'closed')
  }

  /**
   * @param {Event} e
   * @returns {void} Resolves to `undefined`
   */
  async saveSourceEditor (e) {
    const $editorDialog = $id('se-svg-editor-dialog')
    if ($editorDialog.getAttribute('dialog') !== 'open') return
    // Centring is deliberately NOT part of saveChanges: zoomImage() centres against the
    // resolution current at the moment it runs, and enforceDocumentDimensionsAndFitContent()
    // changes that resolution via setResolution(). Centring first left the view belonging to
    // the previous page, so the artwork appeared at the top-left until the next zoom action
    // recomputed it (BUG-208). Centre last, once the page size is settled.
    const saveChanges = () => {
      this.svgCanvas.clearSelection()
      this.hideSourceEditor()
      this.layersPanel.populateLayers()
    }

    if (!this.svgCanvas.setSvgString(e.detail.value)) {
      const ok = await seConfirm(
        this.i18next.t('notification.QerrorsRevertToSource')
      )
      if (ok === false || ok === 'Cancel') {
        return
      }
      saveChanges()
      this.zoomImage()
      return
    }
    saveChanges()
    this.enforceDocumentDimensionsAndFitContent()
    this.zoomImage()
    this.leftPanel.clickSelect()
  }

  /**
   * @param {Event} e
   * @returns {void} Resolves to `undefined`
   */
  cancelOverlays (e) {
    if ($id('dialog_box') != null) $id('dialog_box').style.display = 'none'
    const $editorDialog = $id('se-svg-editor-dialog')
    const editingsource = $editorDialog.getAttribute('dialog') === 'open'
    if (!editingsource && !this.docprops && !this.configObj.preferences) {
      if (this.curContext) {
        this.svgCanvas.leaveContext()
      }
      return
    }

    if (editingsource) {
      const origSource = this.svgCanvas.getSvgString()
      if (origSource !== e.detail.value) {
        const ok = seConfirm(
          this.i18next.t('notification.QignoreSourceChanges')
        )
        if (ok) {
          this.hideSourceEditor()
        }
      } else {
        this.hideSourceEditor()
      }
    }
  }

  //getSvgContentAsImage(width, height) {
  //  console.log('entering getSvgContentAsImage')
  //  //let svgElementString = this.svgCanvas.getSvgString()
  //  //console.log('svgElementString from getSvgContentAsImage:' + svgElementString)
  //  // Create a new image element
  //  let img = new Image();

  //  // Serialize the SVG element to a string
  //  let serializer = new XMLSerializer();
  //  const svgRoot = $id('svgroot')
  //  let svgString = serializer.serializeToString(svgRoot);
  //  console.log('svgString from getSvgContentAsImage:' + svgString)

  //  console.log('Create a Blob from the SVG string')
  //  let blob = new Blob([svgString], { type: "image/svg+xml;charset=utf-8" });

  //  console.log('Create a DOM URL from the Blob')
  //  let domURL = window.URL || window.webkitURL || window;
  //  let url = domURL.createObjectURL(blob);

  //  console.log('Set the width and height of the image')
  //  img.width = width;
  //  img.height = height;

  //  console.log('Set the image source to the created URL')
  //  img.src = url;

  //  return img;
  //}

  getSvgContentResized(percentage) {
    const svgRoot = $id('svgroot')
    const childNodesElems = svgRoot.childNodes
    childNodesElems.forEach((node, i) => {
      if (i && node.nodeType === 8 && node.data.includes('Created with')) {
        this.svgCanvas.getSvgContent().firstChild.before(node)
      }
    })

    // Move out of in-group editing mode
    if (this.svgCanvas.getCurrentGroup()) {
      draw.leaveContext()
      this.svgCanvas.selectOnly([this.svgCanvas.getCurrentGroup()])
    }

    const nakedSvgs = []

    // Unwrap gsvg if it has no special attributes (only id and style)
    const gsvgElems = this.svgCanvas.getSvgContent().querySelectorAll('g[data-gsvg]')
    Array.prototype.forEach.call(gsvgElems, element => {
      const attrs = element.attributes
      let len = attrs.length
      for (let i = 0; i < len; i++) {
        if (attrs[i].nodeName === 'id' || attrs[i].nodeName === 'style') {
          len--
        }
      }
      // No significant attributes, so ungroup
      if (len <= 0) {
        const svg = element.firstChild
        nakedSvgs.push(svg)
        element.replaceWith(svg)
      }
    })
    const output = this.svgCanvas.svgToString(this.svgCanvas.getSvgContent(), 0)
    //console.log('getSvgContentResized output:' + output)
    // Rewrap gsvg
    if (nakedSvgs.length) {
      Array.prototype.forEach.call(nakedSvgs, el => {
        this.svgCanvas.groupSvgElem(el)
      })
    }

    // Scale down the root <svg> width/height by `percentage` (viewBox untouched, so this
    // only shrinks the rendered size — proportions and content are unaffected).
    let resizedOutput = output
    const svgTagMatch = output.match(/<svg\b[^>]*>/)
    if (svgTagMatch) {
      const svgTag = svgTagMatch[0]
      const widthMatch = svgTag.match(/\swidth="([\d.]+)"/)
      const heightMatch = svgTag.match(/\sheight="([\d.]+)"/)
      if (widthMatch && heightMatch) {
        const resizedTag = svgTag
          .replace(widthMatch[0], ` width="${parseFloat(widthMatch[1]) * percentage}"`)
          .replace(heightMatch[0], ` height="${parseFloat(heightMatch[1]) * percentage}"`)
        resizedOutput = output.replace(svgTag, resizedTag)
      }
    }

    return this.svgCanvas.encode64(resizedOutput);
  }


  /**
   * @returns {void}
   */
  toggleDynamicOutput (e) {
    this.configObj.curConfig.dynamicOutput = e.detail.dynamic
    this.svgCanvas.setConfig(this.configObj.curConfig)
    const $editorDialog = document.getElementById('se-svg-editor-dialog')
    const origSource = this.svgCanvas.getSvgString()
    $editorDialog.setAttribute('dialog', 'open')
    $editorDialog.setAttribute('value', origSource)
  }

  /**
   * @returns {void}
   */
  enableOrDisableClipboard () {
    let svgeditClipboard
    try {
      svgeditClipboard = this.localStorage.getItem('svgedit_clipboard')
    } catch (err) {
      /* empty fn */
    }
    this.canvMenu.setAttribute(
      (svgeditClipboard ? 'en' : 'dis') + 'ablemenuitems',
      '#paste,#paste_in_place'
    )
  }

  /**
   * @function module:SVGthis.openPrep
   * @returns {boolean|Promise<boolean>} Resolves to boolean indicating `true` if there were no changes
   *  and `false` after the user confirms.
   */
  async openPrep () {
    if (this.svgCanvas.undoMgr.getUndoStackSize() === 0) {
      return true
    }
    return await seConfirm(this.i18next.t('notification.QwantToOpen'))
  }

  /**
   *
   * @param {Event} e
   * @returns {void}
   */
  onDragEnter (e) {
    e.stopPropagation()
    e.preventDefault()
    // and indicator should be displayed here, such as "drop files here"
  }

  /**
   *
   * @param {Event} e
   * @returns {void}
   */
  onDragOver (e) {
    e.stopPropagation()
    e.preventDefault()
  }

  /**
   *
   * @param {Event} e
   * @returns {void}
   */
  onDragLeave (e) {
    e.stopPropagation()
    e.preventDefault()
    // hypothetical indicator should be removed here
  }

  /**
   * @function module:SVGthis.setLang
   * @param {string} lang The language code
   * @param {module:locale.LocaleStrings} allStrings See {@tutorial LocaleDocs}
   * @fires module:svgcanvas.SvgCanvas#event:ext_langReady
   * @fires module:svgcanvas.SvgCanvas#event:ext_langChanged
   * @returns {void} A Promise which resolves to `undefined`
   */
  setLang (lang) {
    this.langChanged = true
    this.configObj.pref('lang', lang)
    const $editDialog = $id('se-edit-prefs')
    $editDialog.setAttribute('lang', lang)
    const oldLayerName = $id('#layerlist')
      ? $id('#layerlist').querySelector('tr.layersel td.layername').textContent
      : ''
    const renameLayer =
      oldLayerName === this.i18next.t('notification.common.layer') + ' 1'

    this.setTitles()

    if (renameLayer) {
      this.svgCanvas.renameCurrentLayer(
        this.i18next.t('notification.common.layer') + ' 1'
      )
      this.layersPanel.populateLayers()
    }

    this.svgCanvas.runExtensions(
      'langChanged',
      /** @type {module:svgcanvas.SvgCanvas#event:ext_langChanged} */ lang
    )
  }

    b64toBlob = (b64Data, contentType = '', sliceSize = 512) => {
    const byteCharacters = atob(b64Data)
    const byteArrays = []
    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize)
      const byteNumbers = new Array(slice.length)
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i)
      }
      const byteArray = new Uint8Array(byteNumbers)
      byteArrays.push(byteArray)
    }
    const blob = new Blob(byteArrays, { type: contentType })
    return blob
  }

  /**
   * @callback module:SVGthis.ReadyCallback
   * @returns {Promise<void>|void}
   */
  /**
   * Queues a callback to be invoked when the editor is ready (or
   *   to be invoked immediately if it is already ready--i.e.,
   *   if `runCallbacks` has been run).
   * @function module:SVGthis.ready
   * @param {module:SVGthis.ReadyCallback} cb Callback to be queued to invoke
   * @returns {Promise<ArbitraryCallbackResult>} Resolves when all callbacks, including the supplied have resolved
   */
  ready (cb) {
    return new Promise((resolve, reject) => {
      if (this.isReady) {
        resolve(cb())
        return
      }
      this.callbacks.push([cb, resolve, reject])
    })
  }

  /**
   * Invokes the callbacks previous set by `svgthis.ready`
   * @function module:SVGthis.runCallbacks
   * @returns {Promise<void>} Resolves to `undefined` if all callbacks succeeded and rejects otherwise
   */
  async runCallbacks () {
    try {
      await Promise.all(
        this.callbacks.map(([cb]) => {
          return cb()
        })
      )
    } catch (err) {
      this.callbacks.forEach(([, , reject]) => {
        reject()
      })
      throw err
    }
    this.callbacks.forEach(([, resolve]) => {
      resolve()
    })
    this.isReady = true
  }

  /**
   * @function module:SVGthis.loadFromString
   * @param {string} str The SVG string to load
   * @param {PlainObject} [opts={}]
   * @param {boolean} [opts.noAlert=false] Option to avoid alert to user and instead get rejected promise
   * @returns {Promise<void>}
   */
  loadFromString (str, { noAlert } = {}) {
    return this.ready(async () => {
      try {
        await this.loadSvgString(str, { noAlert })
      } catch (err) {
        if (noAlert) {
          throw err
        }
      }
    })
  }

  /**
   * @callback module:SVGthis.URLLoadCallback
   * @param {boolean} success
   * @returns {void}
   */
  /**
   * @function module:SVGthis.loadFromURL
   * @param {string} url URL from which to load an SVG string via Ajax
   * @param {PlainObject} [opts={}] May contain properties: `cache`, `callback`
   * @param {boolean} [opts.cache]
   * @param {boolean} [opts.noAlert]
   * @returns {Promise<void>} Resolves to `undefined` or rejects upon bad loading of
   *   the SVG (or upon failure to parse the loaded string) when `noAlert` is
   *   enabled
   */
  loadFromURL (url, { cache, noAlert } = {}) {
    return this.ready(() => {
      return new Promise((resolve, reject) => {
        fetch(url, { cache: cache ? 'force-cache' : 'no-cache' })
          .then(response => {
            if (!response.ok) {
              if (noAlert) {
                reject(new Error('URLLoadFail'))
                return
              }
              seAlert(this.i18next.t('notification.URLLoadFail'))
              resolve()
            }
            return response.text()
          })
          .then(str => {
            this.loadSvgString(str, { noAlert })
            return str
          })
          .catch(error => {
            if (noAlert) {
              reject(new Error('URLLoadFail'))
              return
            }
            seAlert(this.i18next.t('notification.URLLoadFail') + ': \n' + error)
            resolve()
          })
      })
    })
  }

  /**
   * @function module:SVGthis.loadFromDataURI
   * @param {string} str The Data URI to base64-decode (if relevant) and load
   * @param {PlainObject} [opts={}]
   * @param {boolean} [opts.noAlert]
   * @returns {Promise<void>} Resolves to `undefined` and rejects if loading SVG string fails and `noAlert` is enabled
   */
  loadFromDataURI (str, { noAlert } = {}) {
    return this.ready(() => {
      let base64 = false
      let pre = str.match(/^data:image\/svg\+xml;base64,/)
      if (pre) {
        base64 = true
      } else {
        pre = str.match(/^data:image\/svg\+xml(?:;|;utf8)?,/)
      }
      if (pre) {
        pre = pre[0]
      }
      const src = str.slice(pre.length)
      return this.loadSvgString(
        base64 ? decode64(src) : decodeURIComponent(src),
        { noAlert }
      )
    })
  }

  /**
   * @function module:SVGthis.addExtension
   * @param {string} name Used internally; no need for i18n.
   * @param {module:svgcanvas.ExtensionInitCallback} initfn Config to be invoked on this module
   * @param {module:svgcanvas.ExtensionInitArgs} initArgs
   * @throws {Error} If called too early
   * @returns {Promise<void>} Resolves to `undefined`
   */
  addExtension (name, initfn, initArgs) {
    // Note that we don't want this on this.ready since some extensions
    // may want to run before then (like server_opensave).
    if (!this.svgCanvas) {
      throw new Error('Extension added too early')
    }
    return this.svgCanvas.addExtension(name, initfn, initArgs)
  }
}

export default Editor
